import type { FilterConfig } from '@/types/filters'

/**
 * LOTE 4: Configurações de filtros para benefícios e remuneração
 * 
 * BENEFÍCIOS (8 campos):
 * - Plano de Saúde (boolean + provider + data inclusão)
 * - Benefício Caju (boolean)
 * - Benefício Wellhub (boolean) 
 * - Benefício Zelo (boolean)
 * - Benefício Guapeco (boolean)
 * - Vale Transporte (number)
 * 
 * REMUNERAÇÃO (4 campos):
 * - Último Salário (number)
 * - Tipo Salário (CLT/PJ)  
 * - Data Último Salário (date)
 * - Meses sem Aumento (number)
 */

export const LOTE_4_FILTER_CONFIGS: FilterConfig[] = [
  // ===== BENEFÍCIOS =====
  {
    columnId: 'hasHealthInsurance',
    label: 'Possui Plano de Saúde',
    type: 'boolean',
    operators: ['equals'],
    options: [
      { value: 'true', label: 'Sim' },
      { value: 'false', label: 'Não' }
    ],
    placeholder: 'Possui plano de saúde...',
    category: 'Benefícios'
  },
  {
    columnId: 'healthInsuranceProvider',
    label: 'Operadora do Plano',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains', 'isEmpty', 'isNotEmpty'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione a operadora...',
    category: 'Benefícios'
  },
  {
    columnId: 'healthInsuranceInclusionDate',
    label: 'Data Inclusão Plano',
    type: 'date',
    operators: ['equals', 'greaterThan', 'lessThan', 'between', 'isEmpty', 'isNotEmpty'],
    placeholder: 'dd/mm/aaaa',
    category: 'Benefícios'
  },
  {
    columnId: 'hasCajuBenefit',
    label: 'Benefício Caju',
    type: 'boolean',
    operators: ['equals'],
    options: [
      { value: 'true', label: 'Ativo' },
      { value: 'false', label: 'Inativo' }
    ],
    placeholder: 'Possui benefício Caju...',
    category: 'Benefícios'
  },
  {
    columnId: 'hasWellhubBenefit',
    label: 'Benefício Wellhub',
    type: 'boolean',
    operators: ['equals'],
    options: [
      { value: 'true', label: 'Ativo' },
      { value: 'false', label: 'Inativo' }
    ],
    placeholder: 'Possui benefício Wellhub...',
    category: 'Benefícios'
  },
  {
    columnId: 'hasZeloBenefit',
    label: 'Benefício Zelo',
    type: 'boolean',
    operators: ['equals'],
    options: [
      { value: 'true', label: 'Ativo' },
      { value: 'false', label: 'Inativo' }
    ],
    placeholder: 'Possui benefício Zelo...',
    category: 'Benefícios'
  },
  {
    columnId: 'hasGuapecoBenefit',
    label: 'Benefício Guapeco',
    type: 'boolean',
    operators: ['equals'],
    options: [
      { value: 'true', label: 'Ativo' },
      { value: 'false', label: 'Inativo' }
    ],
    placeholder: 'Possui benefício Guapeco...',
    category: 'Benefícios'
  },
  {
    columnId: 'transportationBenefitAmount',
    label: 'Valor Vale Transporte',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between', 'isEmpty', 'isNotEmpty'],
    placeholder: 'Ex: 150.00',
    category: 'Benefícios'
  },
  
  // ===== REMUNERAÇÃO =====
  {
    columnId: 'lastSalary',
    label: 'Último Salário',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between'],
    placeholder: 'Ex: 5000.00',
    category: 'Remuneração'
  },
  {
    columnId: 'lastSalaryType',
    label: 'Tipo do Último Salário',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'CLT', label: 'CLT' },
      { value: 'PJ', label: 'Pessoa Jurídica' },
      { value: 'INTERN', label: 'Estágio' },
      { value: 'APPRENTICE', label: 'Aprendiz' }
    ],
    placeholder: 'Selecione o tipo...',
    category: 'Remuneração'
  },
  {
    columnId: 'lastSalaryDate',
    label: 'Data do Último Salário',
    type: 'date',
    operators: ['equals', 'greaterThan', 'lessThan', 'between'],
    placeholder: 'dd/mm/aaaa',
    category: 'Remuneração'
  },
  {
    columnId: 'timeSinceLastRaise',
    label: 'Meses sem Aumento',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between'],
    placeholder: 'Ex: 12',
    category: 'Remuneração'
  }
]

/**
 * Acessores para campos do Lote 4
 * Campos que precisam de transformação ou cálculos especiais
 */
export const LOTE_4_FIELD_ACCESSORS = {
  // Benefícios diretos
  hasHealthInsurance: (item: any) => item.hasHealthInsurance,
  healthInsuranceProvider: (item: any) => item.healthInsuranceProvider,
  healthInsuranceInclusionDate: (item: any) => item.healthInsuranceInclusionDate,
  hasCajuBenefit: (item: any) => item.hasCajuBenefit,
  hasWellhubBenefit: (item: any) => item.hasWellhubBenefit,
  hasZeloBenefit: (item: any) => item.hasZeloBenefit,
  hasGuapecoBenefit: (item: any) => item.hasGuapecoBenefit,
  transportationBenefitAmount: (item: any) => item.transportationBenefitAmount,
  
  // Campos de salário que podem precisar de cálculo
  lastSalary: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.lastSalary !== undefined) return item.lastSalary
    
    // Senão, calcular do array de salários
    const salaries = item.salaryHistorySalaries || []
    if (!salaries.length) return null
    
    const now = new Date()
    const pastSalaries = salaries.filter((salary: any) => new Date(salary.date) <= now)
    if (!pastSalaries.length) return null
    
    const sortedSalaries = [...pastSalaries].sort((a: any, b: any) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )
    
    return sortedSalaries[0]?.amount || null
  },
  
  lastSalaryType: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.lastSalaryType !== undefined) return item.lastSalaryType
    
    // Senão, calcular do array de salários
    const salaries = item.salaryHistorySalaries || []
    if (!salaries.length) return null
    
    const now = new Date()
    const pastSalaries = salaries.filter((salary: any) => new Date(salary.date) <= now)
    if (!pastSalaries.length) return null
    
    const sortedSalaries = [...pastSalaries].sort((a: any, b: any) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )
    
    return sortedSalaries[0]?.salaryType || null
  },
  
  lastSalaryDate: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.lastSalaryDate !== undefined) return item.lastSalaryDate
    
    // Senão, calcular do array de salários
    const salaries = item.salaryHistorySalaries || []
    if (!salaries.length) return null
    
    const now = new Date()
    const pastSalaries = salaries.filter((salary: any) => new Date(salary.date) <= now)
    if (!pastSalaries.length) return null
    
    const sortedSalaries = [...pastSalaries].sort((a: any, b: any) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )
    
    return sortedSalaries[0]?.date || null
  },
  
  timeSinceLastRaise: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.timeSinceLastRaise !== undefined) return item.timeSinceLastRaise
    
    // Senão, calcular do array de salários
    const salaries = item.salaryHistorySalaries || []
    if (!salaries.length) return null
    
    const now = new Date()
    const pastSalaries = salaries.filter((salary: any) => new Date(salary.date) <= now)
    if (!pastSalaries.length) return null
    
    const sortedSalaries = [...pastSalaries].sort((a: any, b: any) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )
    
    const lastSalaryDate = new Date(sortedSalaries[0]?.date)
    const monthsDiff = Math.floor((now.getTime() - lastSalaryDate.getTime()) / (1000 * 60 * 60 * 24 * 30))
    
    return monthsDiff >= 0 ? monthsDiff : 0
  }
}

/**
 * Presets estratégicos para Lote 4
 */
export const LOTE_4_PRESETS = [
  {
    id: 'colaboradores_sem_plano_saude',
    name: 'Sem Plano de Saúde',
    description: 'Colaboradores ativos sem plano de saúde ativo',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'hasHealthInsurance',
        operator: 'equals' as const,
        value: 'false',
        type: 'boolean' as const,
        label: 'Possui Plano de Saúde',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'colaboradores_salario_alto',
    name: 'Salários Acima de R$ 10.000',
    description: 'Colaboradores com último salário superior a R$ 10.000',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'lastSalary',
        operator: 'greaterThan' as const,
        value: 10000,
        type: 'number' as const,
        label: 'Último Salário',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'colaboradores_sem_aumento_12_meses',
    name: 'Sem Aumento há mais de 12 Meses',
    description: 'Colaboradores que não recebem aumento há mais de um ano',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'timeSinceLastRaise',
        operator: 'greaterThan' as const,
        value: 12,
        type: 'number' as const,
        label: 'Meses sem Aumento',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'colaboradores_beneficios_completos',
    name: 'Pacote Completo de Benefícios',
    description: 'Colaboradores com todos os principais benefícios ativos',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'hasHealthInsurance',
        operator: 'equals' as const,
        value: 'true',
        type: 'boolean' as const,
        label: 'Possui Plano de Saúde',
        enabled: true
      },
      {
        columnId: 'hasCajuBenefit',
        operator: 'equals' as const,
        value: 'true',
        type: 'boolean' as const,
        label: 'Benefício Caju',
        enabled: true
      },
      {
        columnId: 'hasWellhubBenefit',
        operator: 'equals' as const,
        value: 'true',
        type: 'boolean' as const,
        label: 'Benefício Wellhub',
        enabled: true
      }
    ],
    isSystem: true
  }
] as const